import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';

// Use the same logo for both modes
const LOGO_URL = "https://customer-assets.emergentagent.com/job_f7db9eb5-303b-40e1-ba38-b76b077cd7ba/artifacts/fty4yugx_Utility%20logo_page-0001%20%281%29.jpg";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Compare', path: '/compare' },
  { name: 'Industries', path: '/industries' },
  { name: 'Locations', path: '/locations' },
  { name: 'Safety', path: '/safety' },
  { name: 'Leadership', path: '/about' },
  { name: 'Careers', path: '/careers' },
  { name: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-skytec-surface shadow-md border-b border-skytec-border"
    >
      <div className="w-full px-6 lg:px-12">
        <div className="flex items-center justify-between h-32">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <img 
              src={LOGO_URL} 
              alt="USG Logo" 
              className="h-28 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg ${
                  location.pathname === link.path
                    ? 'text-skytec-primary bg-skytec-primary-dim'
                    : 'text-skytec-fg/80 hover:text-skytec-primary hover:bg-skytec-primary-dim/50'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-skytec-primary-dim hover:bg-skytec-primary/20 transition-colors"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-skytec-secondary" />
              ) : (
                <Moon className="w-5 h-5 text-skytec-primary" />
              )}
            </button>
            <Link to="/contact">
              <Button 
                data-testid="navbar-cta-quote"
                className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading font-semibold px-6"
              >
                GET A QUOTE
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-skytec-fg hover:text-skytec-primary transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-skytec-surface border-t border-skytec-border"
          >
            <div className="px-4 py-6 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`block px-4 py-3 rounded-lg font-medium transition-all ${
                    location.pathname === link.path
                      ? 'text-skytec-primary bg-skytec-primary-dim'
                      : 'text-skytec-fg/80 hover:text-skytec-primary hover:bg-skytec-primary-dim/50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 space-y-3">
                {/* Theme Toggle for Mobile */}
                <button
                  onClick={toggleTheme}
                  className="w-full flex items-center justify-center py-3 px-4 rounded-lg bg-skytec-primary-dim hover:bg-skytec-primary/20 transition-colors"
                  data-testid="mobile-theme-toggle"
                >
                  {theme === 'dark' ? (
                    <>
                      <Sun className="w-5 h-5 mr-2 text-skytec-secondary" />
                      <span className="text-skytec-fg">Light Mode</span>
                    </>
                  ) : (
                    <>
                      <Moon className="w-5 h-5 mr-2 text-skytec-primary" />
                      <span className="text-skytec-fg">Dark Mode</span>
                    </>
                  )}
                </button>
                <Link to="/contact" className="block">
                  <Button 
                    className="w-full bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading font-semibold"
                    data-testid="mobile-cta-quote"
                  >
                    GET A QUOTE
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
